import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Card, CardContent, Chip, Button,
  IconButton, CircularProgress, Alert, Pagination, FormControl, InputLabel,
  Select, MenuItem, TextField, InputAdornment, Divider, Grid, Tooltip,
  Breadcrumbs, Link,
} from '@mui/material';
import {
  Search, FilterList, Refresh, CalendarToday, Article, Public,
  ExpandMore, ExpandLess, Fullscreen, Home, NavigateNext,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { fetchCorpus, fetchStats, fetchKeywordDetail, fetchCorpusDetail, fetchKeywordYears, fetchKeywordFilters } from '../utils/api';
import { colors } from '../styles/theme';

const SENTIMENT_COLORS = {
  positive: '#2D5A4A', negative: '#9B2C2C', neutral: '#6B7280', mixed: '#C9A227',
};
const SENTIMENT_LABELS = {
  positive: '积极', negative: '消极', neutral: '中性', mixed: '混合',
};

function CorpusList() {
  const { keywordId } = useParams();
  const navigate = useNavigate();

  const [corpus, setCorpus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState(null);
  const [stats, setStats] = useState({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSentiment, setSelectedSentiment] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [keywordYears, setKeywordYears] = useState([]);
  const [keywordTopics, setKeywordTopics] = useState([]);
  const [keywordSentiments, setKeywordSentiments] = useState([]);

  const [expandedId, setExpandedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    loadStats();
    if (keywordId) loadKeywordDetail();
  }, [keywordId]);

  useEffect(() => {
    loadCorpus();
  }, [keywordId, page, pageSize, selectedTopic, selectedSentiment, selectedYear]);

  const loadStats = async () => {
    try { setStats(await fetchStats()); } catch (e) { console.error(e); }
  };

  const loadKeywordDetail = async () => {
    try {
      const data = await fetchKeywordDetail(keywordId);
      setKeyword(data);
      setKeywordYears(await fetchKeywordYears(keywordId));
      const filters = await fetchKeywordFilters(keywordId);
      setKeywordTopics(filters.topics || []);
      setKeywordSentiments(filters.sentiments || []);
    } catch (e) { console.error(e); }
  };

  const loadCorpus = async () => {
    setLoading(true);
    try {
      const params = {
        keyword_id: keywordId ? parseInt(keywordId) : undefined,
        page, page_size: pageSize,
      };
      if (selectedTopic) params.topic = selectedTopic;
      if (selectedSentiment) params.sentiment = selectedSentiment;
      if (selectedYear) params.year = parseInt(selectedYear);
      if (searchQuery) params.search = searchQuery;
      const data = await fetchCorpus(params);
      setCorpus(data.items);
      setTotal(data.total);
    } catch (e) {
      setError('加载语料失败');
    } finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadCorpus(); };
  const handleReset = () => {
    setSearchQuery(''); setSelectedTopic(''); setSelectedSentiment(''); setSelectedYear('');
    setPage(1); loadCorpus();
  };
  const handlePageChange = (e, v) => { setPage(v); window.scrollTo({top:0,behavior:'smooth'}); };

  const handleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setDetailData(null); return; }
    setExpandedId(id); setDetailLoading(true);
    try { setDetailData(await fetchCorpusDetail(id)); }
    catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const getSentimentColor = (s) => s ? (SENTIMENT_COLORS[s.toLowerCase()] || colors.text.secondary) : colors.text.secondary;
  const getSentimentLabel = (s) => s ? (SENTIMENT_LABELS[s.toLowerCase()] || s) : '未知';

  const yearOptions = keywordYears.map(y => ({ year: y.year, count: y.count }));

  return (
    <Box sx={{ minHeight:'100vh', pb:8, backgroundColor:colors.background.default }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, ' + colors.primary.dark + ' 0%, ' + colors.primary.main + ' 50%, ' + colors.primary.light + ' 100%)',
        color:'white', py:4, mb:4,
      }}>
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNext sx={{ fontSize:16, color:'rgba(255,255,255,0.6)' }} />}
            sx={{ mb:2 }}
          >
            <Link component={RouterLink} to="/" sx={{
              display:'flex', alignItems:'center', gap:0.5,
              color:'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:'0.85rem',
              '&:hover':{ color:'white' },
            }}>
              <Home sx={{ fontSize:16 }} /> 首页
            </Link>
            {keyword && (
              <Link component={RouterLink} to={"/keyword/" + keywordId} sx={{
                color:'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:'0.85rem',
                '&:hover':{ color:'white' },
              }}>
                {keyword.chinese_name}
              </Link>
            )}
            <Typography sx={{ color:'white', fontSize:'0.85rem', fontWeight:600 }}>
              语料列表
            </Typography>
          </Breadcrumbs>

          {keyword && (
            <Box>
              <Typography variant="h4" sx={{ fontWeight:600, fontFamily:'"Noto Serif SC",serif', mb:1 }}>
                {keyword.chinese_name} · 语料列表
              </Typography>
              <Typography variant="body1" sx={{ opacity:0.9, fontStyle:'italic', mb:2 }}>
                {keyword.english_translations}
              </Typography>
              <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
                <Chip icon={<CalendarToday sx={{color:'white !important'}} />}
                  label={keyword.year + '年'}
                  sx={{backgroundColor:'rgba(255,255,255,0.2)',color:'white'}} />
                <Chip label={total.toLocaleString() + ' 条语料'}
                  sx={{backgroundColor:'rgba(255,255,255,0.2)',color:'white'}} />
              </Box>
            </Box>
          )}
          {!keyword && (
            <Typography variant="h4" sx={{ fontWeight:600, fontFamily:'"Noto Serif SC",serif' }}>
              语料列表
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Filters */}
        <Paper sx={{ p:3, mb:3, borderRadius:3, boxShadow:'0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" sx={{ mb:2, fontWeight:600, display:'flex', alignItems:'center', gap:1 }}>
            <FilterList sx={{color:colors.primary.main}} /> 筛选条件
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField fullWidth placeholder="搜索句子、标题或翻译..." value={searchQuery}
                onChange={(e)=>setSearchQuery(e.target.value)}
                onKeyPress={(e)=>e.key==='Enter'&&handleSearch()}
                InputProps={{startAdornment:(<InputAdornment position="start"><Search sx={{color:colors.primary.main}}/></InputAdornment>)}} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth><InputLabel>主题</InputLabel>
                <Select value={selectedTopic} onChange={(e)=>setSelectedTopic(e.target.value)} label="主题">
                  <MenuItem value="">全部</MenuItem>
                  {(keywordId && keywordTopics.length > 0 ? keywordTopics : (stats.topics || []).map(t => ({name: t, count: null}))).map(t=><MenuItem key={t.name} value={t.name}>{t.name}{t.count != null ? ` (${t.count})` : ''}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth><InputLabel>情感</InputLabel>
                <Select value={selectedSentiment} onChange={(e)=>setSelectedSentiment(e.target.value)} label="情感">
                  <MenuItem value="">全部</MenuItem>
                  {(keywordId && keywordSentiments.length > 0 ? keywordSentiments : (stats.sentiments || []).map(s => ({name: s, count: null}))).map(s=><MenuItem key={s.name} value={s.name}>{getSentimentLabel(s.name)}{s.count != null ? ` (${s.count})` : ''}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth><InputLabel>年份</InputLabel>
                <Select value={selectedYear} onChange={(e)=>setSelectedYear(e.target.value)} label="年份">
                  <MenuItem value="">全部</MenuItem>
                  {yearOptions.map(({year,count})=><MenuItem key={year} value={year}>{year}年 ({count}条)</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{display:'flex',gap:1}}>
                <Button variant="contained" onClick={handleSearch} startIcon={<Search/>}
                  sx={{backgroundColor:colors.primary.main,'&:hover':{backgroundColor:colors.primary.dark}}}>搜索</Button>
                <Button variant="outlined" onClick={handleReset} startIcon={<Refresh/>}>重置</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Corpus List */}
        {loading ? (
          <Box sx={{display:'flex',justifyContent:'center',py:8}}><CircularProgress size={48}/></Box>
        ) : error ? (
          <Alert severity="error" sx={{mb:3}}>{error}</Alert>
        ) : (
          <>
            <Box sx={{mb:2,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <Typography variant="body1" color="text.secondary">
                共 {total.toLocaleString()} 条语料 | 第 {page} 页，每页 {pageSize} 条
              </Typography>
              <FormControl size="small" sx={{minWidth:100}}>
                <Select value={pageSize} onChange={(e)=>{setPageSize(e.target.value);setPage(1);}}>
                  <MenuItem value={10}>10条/页</MenuItem>
                  <MenuItem value={20}>20条/页</MenuItem>
                  <MenuItem value={50}>50条/页</MenuItem>
                  <MenuItem value={100}>100条/页</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {corpus.length === 0 && (
              <Box sx={{textAlign:'center',py:6}}>
                <Typography color="text.secondary" sx={{mb:2}}>当前筛选条件下无语料</Typography>
                <Button variant="outlined" onClick={handleReset}>重置筛选</Button>
              </Box>
            )}

            <Box sx={{display:'flex',flexDirection:'column',gap:2}}>
              {corpus.map((item) => (
                <Card key={item.id} onClick={()=>handleExpand(item.id)} sx={{
                  borderRadius:2, boxShadow:'0 2px 8px rgba(0,0,0,0.06)', cursor:'pointer',
                  transition:'all 0.3s ease',
                  '&:hover':{boxShadow:'0 4px 16px rgba(0,0,0,0.12)'},
                  border:expandedId===item.id ? '2px solid ' + colors.primary.main : 'none',
                }}>
                  <CardContent sx={{p:3}}>
                    <Typography variant="body1" sx={{
                      fontSize:'1.1rem', lineHeight:1.8, mb:2,
                      fontFamily:'"Noto Serif SC", Georgia, serif',
                    }}>
                      &ldquo;{item.original_sentence}&rdquo;
                    </Typography>

                    {item.english_translation && (
                      <Typography variant="body2" sx={{
                        color:colors.primary.main, fontStyle:'italic', mb:2, pl:2,
                        borderLeft:'3px solid ' + colors.primary.light,
                      }}>{item.english_translation}</Typography>
                    )}

                    <Divider sx={{my:2}} />

                    <Box sx={{display:'flex',flexWrap:'wrap',gap:1,mb:2}}>
                      {item.sentence_topic_1 && (
                        <Chip label={item.sentence_topic_1} size="small" sx={{backgroundColor:colors.accent.cool,fontWeight:500}} />
                      )}
                      {item.sentence_sentiment_1 && (
                        <Chip label={getSentimentLabel(item.sentence_sentiment_1)} size="small" sx={{
                          backgroundColor:getSentimentColor(item.sentence_sentiment_1)+'20',
                          color:getSentimentColor(item.sentence_sentiment_1), fontWeight:500,
                        }} />
                      )}
                      {item.sentence_sentiment_1_prob && (
                        <Tooltip title="情感置信度">
                          <Chip label={(item.sentence_sentiment_1_prob*100).toFixed(1)+'%'} size="small" variant="outlined" />
                        </Tooltip>
                      )}
                      <Chip
                        icon={expandedId===item.id ? <ExpandLess/> : <ExpandMore/>}
                        label={expandedId===item.id ? '收起' : '查看原文'}
                        size="small" color="primary" variant="outlined" sx={{ml:'auto'}} />
                    </Box>

                    <Box sx={{display:'flex',flexWrap:'wrap',gap:2,alignItems:'center'}}>
                      <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                        <Article sx={{fontSize:16,color:colors.text.secondary}} />
                        <Typography variant="body2" color="text.secondary" noWrap sx={{maxWidth:300}}>
                          {item.news_title || '无标题'}
                        </Typography>
                      </Box>
                      <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                        <Public sx={{fontSize:16,color:colors.text.secondary}} />
                        <Typography variant="body2" color="text.secondary">{item.news_source||'未知来源'}</Typography>
                      </Box>
                      {item.news_date && (
                        <Chip icon={<CalendarToday sx={{fontSize:14}}/>} label={item.news_date}
                          size="small" variant="outlined" sx={{height:24}} />
                      )}
                    </Box>

                    {/* Expanded Detail */}
                    {expandedId===item.id && (
                      <Box sx={{mt:3,pt:3,borderTop:'1px dashed #ddd'}}>
                        <Typography variant="h6" sx={{mb:2,fontWeight:600,color:colors.primary.main}}>
                          <Fullscreen sx={{fontSize:20,verticalAlign:'middle',mr:1}} />
                          新闻原文
                        </Typography>
                        {detailLoading ? (
                          <Box sx={{display:'flex',justifyContent:'center',py:4}}><CircularProgress size={32}/></Box>
                        ) : detailData ? (
                          <Box>
                            <Paper sx={{p:2,mb:2,backgroundColor:colors.background.paper}}>
                              <Typography variant="subtitle1" sx={{fontWeight:600,mb:1}}>
                                {detailData.news_title||'无标题'}
                              </Typography>
                              <Box sx={{display:'flex',gap:2,flexWrap:'wrap'}}>
                                <Typography variant="body2" color="text.secondary">来源: {detailData.news_source||'未知'}</Typography>
                                <Typography variant="body2" color="text.secondary">日期: {detailData.news_date||'未知'}</Typography>
                                {detailData.news_year && <Typography variant="body2" color="text.secondary">年份: {detailData.news_year}</Typography>}
                              </Box>
                            </Paper>
                            {detailData.news_full_text ? (
                              <Paper sx={{p:3,backgroundColor:colors.background.paper}}>
                                <Typography variant="body1" sx={{lineHeight:2,fontFamily:'"Noto Serif SC", Georgia, serif',whiteSpace:'pre-wrap'}}>
                                  {detailData.news_full_text}
                                </Typography>
                              </Paper>
                            ) : (
                              <Alert severity="info" sx={{mt:2}}>暂无完整原文，仅显示当前句子</Alert>
                            )}
                            {detailData.keyword_name && (
                              <Box sx={{mt:2}}>
                                <Chip label={'关联热词: ' + detailData.keyword_name} color="primary" variant="outlined" />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Alert severity="error">加载详情失败</Alert>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>

            {total > pageSize && (
              <Box sx={{display:'flex',justifyContent:'center',mt:4}}>
                <Pagination count={Math.ceil(total/pageSize)} page={page}
                  onChange={handlePageChange} color="primary" size="large"
                  showFirstButton showLastButton />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default CorpusList;
